import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import IdleTimer from "react-idle-timer";
import { useLocation, useNavigate } from "react-router-dom";
import {
  KIOSK_HOME_ROUTE,
  KIOSK_IDLE_LOGOUT_COUNTDOWN_SECONDS,
  KIOSK_IDLE_WARNING_MS,
  isKioskAllowedPath,
  isPaymentGatewayCallback,
} from "./kioskConfig.js";
import { clearKioskSession, isKioskSessionActive } from "./kioskSession.js";

/**
 * Mounted once at the app root (see container/MainRoutes.jsx), alongside the
 * existing, untouched <IdleTimerContainer/>. Only does anything while a
 * kiosk session is active (sessionStorage flag set by /kiosk/login or
 * /kiosk/register on success), so it never affects normal site visitors.
 *
 * Responsibilities:
 *  - Idle timeout: warn, then auto-clear the session and return to /kiosk.
 *  - Navigation restriction: if kiosk mode is active and the current route
 *    isn't part of the kiosk-allowed set (kiosk screens + the existing
 *    booking/payment/check-in flow it reuses), redirect back to /kiosk.
 *  - Browser lockdown: full-screen, a back/forward navigation trap, a
 *    refresh/close warning, and blocking the context menu / new tabs / common
 *    keyboard shortcuts for the duration of the session.
 *
 * Deliberately does not modify any existing booking/payment page — this
 * component reuses react-router's location awareness to police navigation
 * from *outside* those pages instead.
 */
const KioskSessionManager = () => {
  const idleTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [active, setActive] = useState(isKioskSessionActive());
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(KIOSK_IDLE_LOGOUT_COUNTDOWN_SECONDS);

  // Broader than `active`: true for the pre-login /kiosk screens too (welcome,
  // login, register), so full-screen engages the moment a guest lands on the
  // kiosk at all, not only after they've signed in.
  const kioskAreaActive = active || location.pathname.toLowerCase().startsWith("/kiosk");

  // Re-check the flag whenever the route changes (covers login just having
  // set it, or a cleanup having just cleared it).
  useEffect(() => {
    setActive(isKioskSessionActive());
  }, [location.pathname]);

  const clearCountdown = useCallback(() => {
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  }, []);

  const endSession = useCallback(async () => {
    clearCountdown();
    setShowWarning(false);
    setActive(false);
    await clearKioskSession(dispatch);
    navigate(KIOSK_HOME_ROUTE, { replace: true });
  }, [clearCountdown, dispatch, navigate]);

  const onIdle = useCallback(() => {
    if (!isKioskSessionActive()) return;

    setShowWarning(true);
    setCountdown(KIOSK_IDLE_LOGOUT_COUNTDOWN_SECONDS);
    clearCountdown();
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          endSession();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearCountdown, endSession]);

  const onActive = useCallback(() => {
    if (showWarning) {
      clearCountdown();
      setShowWarning(false);
    }
  }, [clearCountdown, showWarning]);

  const stayActive = () => {
    idleTimerRef.current?.reset?.();
    clearCountdown();
    setShowWarning(false);
  };

  // Restricted navigation: only enforced while a kiosk session is active.
  useEffect(() => {
    if (!active) return;
    if (isKioskAllowedPath(location.pathname)) return;
    // Let the app's existing payment-gateway return handling (App.jsx) run
    // uninterrupted; it will navigate onward to an allowed route itself.
    if (isPaymentGatewayCallback(location.search)) return;

    navigate(KIOSK_HOME_ROUTE, { replace: true });
  }, [active, location.pathname, location.search, navigate]);

  // Full-screen enforcement: covers the entire kiosk experience, including
  // the pre-login welcome/login/register screens (kioskAreaActive), not just
  // once a guest session is active — "any browser, any window" per the
  // requirement. Re-requests on every tap in case the browser denied an
  // earlier non-gesture attempt, and again if the guest (or OS chrome)
  // exits full-screen while still anywhere in the kiosk area. Harmless no-op
  // inside the Capacitor native shell, which is already full-screen.
  useEffect(() => {
    if (!kioskAreaActive) return undefined;

    const requestFullscreen = () => {
      const el = document.documentElement;
      if (document.fullscreenElement || !el.requestFullscreen) return;
      el.requestFullscreen().catch(() => {});
    };

    requestFullscreen();
    const handlePointerDown = () => requestFullscreen();
    document.addEventListener("pointerdown", handlePointerDown);
    // If the browser/OS drops full-screen (e.g. Esc, external monitor change)
    // while still in the kiosk area, the guest's next tap re-engages it via
    // the pointerdown handler above; also retry immediately on the event.
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) requestFullscreen();
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      if (document.fullscreenElement) {
        document.exitFullscreen?.().catch(() => {});
      }
    };
  }, [kioskAreaActive]);

  // Remaining browser-kiosk lockdown: back/forward trap, refresh/tab-close
  // warning, and blocking the context menu / new tabs / common shortcuts.
  // Only while a real guest session is active (post-login) — these guard the
  // guest's own booking/check-in flow, not the public welcome screen.
  useEffect(() => {
    if (!active) return undefined;

    // Keep the guest on the current history entry instead of letting
    // browser/hardware back-forward navigate out of the kiosk flow.
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
    };
    window.addEventListener("popstate", handlePopState);

    // Refresh/close can't be blocked outright, but this triggers the
    // browser's own native confirmation prompt.
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Block the context menu (right-click / long-press "open in new tab") and
    // any script-initiated popups for the duration of the session.
    const handleContextMenu = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handleContextMenu);

    const originalWindowOpen = window.open;
    window.open = () => null;

    // Block common new-tab/new-window/refresh shortcuts where the browser
    // permits JS to intercept them.
    const handleKeyDown = (e) => {
      const key = e.key.toLowerCase();
      const isNewTabOrWindow = (e.ctrlKey || e.metaKey) && ["t", "n", "w"].includes(key);
      const isRefresh = key === "f5" || ((e.ctrlKey || e.metaKey) && key === "r");
      if (isNewTabOrWindow || isRefresh) {
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("contextmenu", handleContextMenu);
      window.removeEventListener("keydown", handleKeyDown);
      window.open = originalWindowOpen;
    };
  }, [active]);

  useEffect(() => () => clearCountdown(), [clearCountdown]);

  if (!active) return null;

  return (
    <>
      <IdleTimer
        ref={idleTimerRef}
        timeout={KIOSK_IDLE_WARNING_MS}
        onIdle={onIdle}
        onActive={onActive}
        debounce={500}
      />

      {showWarning && (
        <div
          role="alertdialog"
          aria-live="assertive"
          className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-900/60 px-4"
        >
          <div className="w-full max-w-sm rounded-2xl bg-white p-8 text-center shadow-2xl">
            <h2 className="text-xl font-bold text-slate-800">Still there?</h2>
            <p className="mt-2 text-sm text-slate-500">
              This kiosk will reset for the next guest in {countdown} second{countdown === 1 ? "" : "s"}.
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <button
                type="button"
                onClick={stayActive}
                className="w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                I'm still here
              </button>
              <button
                type="button"
                onClick={endSession}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                End session now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default KioskSessionManager;
